/**
 * S-16 «Остатки» и S-17 «План поставок». Источник — конверт агента «Остатки и заказы»:
 * запас в днях, продажи в день за дни наличия, статус Wildberries (availability) словарём.
 * Разреза по складам WB нет у всех — так и написано, со ссылкой «Почему этого нет».
 */
import { useState } from 'react'
import { fetchStock, type Stock, type StockItem } from '../api/sections.js'
import { Icon } from '../components/icons/Icon.js'
import { Failed, Loading, NoShop, Empty, useData, useScreenHead, useRole } from './common.js'
import { Kv, Num, Proposals, Sect, ToneTag } from './ui.js'
import { itemCode, windowWords } from './words.js'
import type { ScreenId } from '../router/screens.js'

type Open = (id: ScreenId, param?: string) => void
type Filter = 'all' | 'low' | 'over'

/** Окна темпа продаж: пресеты и предел своего ввода (тот же, что на сервере). */
const WINDOWS = [3, 7, 14, 30] as const
const WINDOW_MAX = 30

function StockRow({ i }: { i: StockItem }): React.JSX.Element {
  return (
    <div className="row stock-row" data-testid={'stock-' + i.nm_id}>
      <span className="row-main">
        <span className="row-title">{i.title ?? `Артикул WB ${i.nm_id}`}</span>
        <span className="row-meta" data-testid={'stock-code-' + i.nm_id}>{itemCode(i)}</span>
        <span className="camp-tags">
          {i.low_days && <ToneTag tone="risk" word="Скоро закончится" />}
          {i.availability !== null && <span className="hint-inline">по оценке WB: {i.availability.word.toLowerCase()}</span>}
        </span>
        {i.places.map((p, k) => (
          <span className="stock-place" key={k}>
            <span className="stock-place-name">{p.name}{p.size !== null ? `, размер ${p.size}` : ''}</span>
            <span className="stock-place-nums">
              <Num v={p.available} /> · <Num v={p.per_day} /> в день · запас <Num v={p.days_of_stock} />
            </span>
          </span>
        ))}
      </span>
    </div>
  )
}

function useStock(days?: number): ReturnType<typeof useData<Stock>> {
  return useData(() => fetchStock(days), [days])
}

/**
 * За сколько дней считать темп продаж. Админ 27.10.2026: «идёт манипуляция с товаром — хотим
 * видеть темп за последние три-четыре дня, а не за всю неделю». Пресеты и свой ввод, 1–30 дней.
 */
function Window({ days, onDays }: { days: number; onDays: (n: number) => void }): React.JSX.Element {
  const [own, setOwn] = useState('')
  const n = Number(own)
  const ownOk = own !== '' && Number.isInteger(n) && n >= 1 && n <= WINDOW_MAX
  return (
    <section className="card card-pad stock-win" data-testid="stock-window">
      <span className="row-meta" id="stock-win-label">Темп продаж считать за</span>
      <div className="segs" role="group" aria-labelledby="stock-win-label">
        {WINDOWS.map((w) => (
          <button type="button" key={w} className={'seg' + (days === w ? ' seg-on' : '')} aria-pressed={days === w}
            data-testid={'stock-w-' + w} onClick={() => { setOwn(''); onDays(w) }}>{w} дн.</button>
        ))}
        <label className="stock-win-own">
          <span className="vh">Свой период, дней (от 1 до {WINDOW_MAX})</span>
          <input type="number" className="inp" inputMode="numeric" min={1} max={WINDOW_MAX} placeholder="свой"
            value={own} data-testid="stock-w-own" onChange={(e) => setOwn(e.target.value)} />
          {/* Видимое «дн.» рядом с полем: без него пустое поле читается как ещё одна кнопка-пилюля. */}
          <span className="stock-win-unit" aria-hidden="true">дн.</span>
        </label>
        {ownOk && n !== days && (
          <button type="button" className="btn btn-soft" data-testid="stock-w-apply" onClick={() => onDays(n)}>Показать</button>
        )}
      </div>
    </section>
  )
}

export function ScreenStock({ open }: { open: Open }): React.JSX.Element {
  const [days, setDays] = useState(7)
  const { s, reload } = useStock(days)
  const [f, setF] = useState<Filter>('all')
  useScreenHead(undefined, s.kind === 'ok' && s.data.shop !== null ? s.data.period_display : null)
  // Переключатель окна стоит ВЫШЕ состояния загрузки: иначе на время запроса он пропадает
  // и не видно, что именно выбрано.
  const win = <Window days={days} onDays={setDays} />
  if (s.kind === 'loading') return <div data-testid="screen-body">{win}<Loading label="Загрузка остатков" rows={6} /></div>
  if (s.kind === 'fail') return <div data-testid="screen-body">{win}<Failed message={s.message} onRetry={reload} /></div>
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const over = new Set(d.overstock.map((o) => o.nm_id))
  const items = d.items.filter((i) => f === 'all' || (f === 'low' ? i.low_days : over.has(i.nm_id)))
  const seg = (k: Filter, label: string, n: number): React.JSX.Element => (
    <button type="button" className={'seg' + (f === k ? ' seg-on' : '')} aria-pressed={f === k} data-testid={'stock-f-' + k} onClick={() => setF(k)}>
      {label} <span className="seg-n">{n}</span>
    </button>
  )
  return (
    <div data-testid="screen-body">
      {win}
      <div className="segs gap-top" role="group" aria-label="Какие товары показать">
        {seg('all', 'Все', d.summary.items_pcs)}
        {seg('low', 'Мало запаса', d.summary.low_pcs)}
        {seg('over', 'Залежались', d.summary.overstock_pcs)}
      </div>
      <p className="hint">
        «Мало» — запаса меньше чем на {d.low_display ?? '—'}, «залежались» — больше чем на {d.high_display ?? '—'};
        это значения по умолчанию, а не норма Wildberries.
      </p>

      {items.length === 0
        ? <section className="card empty-card"><Empty icon="agent-stock" title={f === 'low' ? 'Товаров с малым запасом нет' : 'Таких товаров нет'} text="Запаса хватает по всем позициям этого списка." /></section>
        : <section className="card" data-testid="stock-list">{items.map((i) => <StockRow key={i.nm_id} i={i} />)}</section>}
      <p className="hint" data-testid="stock-window-note">
        Продажи в день — {windowWords(d.window_days)}, и только за те дни, когда товар был в наличии.
        Запас в днях — остаток, делённый на этот темп.
      </p>
      {d.notes.map((t) => <p className="hint" key={t}>{t}</p>)}

      {!d.warehouse_detail_available && (
        <div className="note" data-testid="stock-blind">
          <Icon name="section-blind" size={20} />
          <span className="note-lines">
            <span>Wildberries скрыл разрез по своим складам с 18 августа 2026 года — у всех одинаково. Ваши склады видны полностью.</span>
            <button type="button" className="link-inline" data-testid="stock-why" onClick={() => open('S-39')}>Почему этого нет</button>
          </span>
        </div>
      )}

      <section className="card gap-top">
        <button type="button" className="row row-tap row-link has-ic" data-testid="stock-plan" onClick={() => open('S-17')}>
          <Icon name="agent-stock" className="ic-action" />
          <span className="row-main"><span className="row-title">План поставок</span></span>
          <Icon name="nav-next" className="ic-chev row-end" />
        </button>
      </section>

      <Proposals items={d.proposals} onReport={(id) => open('S-10', id)} />
    </div>
  )
}

export function ScreenPlan({ open }: { open: Open }): React.JSX.Element {
  // Объём поставки считается по недельному темпу: короткое окно «Остатков» нужно для разбора
  // текущей ситуации, а везти товар по трём дням — слишком резкий сигнал.
  const { s, reload } = useStock()
  const role = useRole()
  useScreenHead(undefined, s.kind === 'ok' && s.data.shop !== null ? s.data.period_display : null)
  if (s.kind === 'loading') return <Loading label="Загрузка плана" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const configured = d.deadlines.lead_time.value !== null
  const rows = d.items.flatMap((i) => i.places.filter((p) => p.scope === 'wb').map((p) => ({ i, p })))
  const toShip = rows.filter((r) => r.p.recommended.value !== null && Number(r.p.recommended.value) > 0)
  return (
    <div data-testid="screen-body">
      {!configured
        ? <section className="card empty-card" data-testid="plan-not-configured">
            <Empty icon="agent-stock" title="Заполните срок поставки и страховой запас"
              text="Без них объём поставки не рассчитать: Штаб не знает, сколько дней едет товар и какой запас держать.">
              {role === 'owner' && <button type="button" className="btn btn-soft" data-testid="plan-setup" onClick={() => open('S-28')}>Заполнить</button>}
            </Empty>
          </section>
        : toShip.length === 0
          ? <section className="card empty-card"><Empty icon="agent-stock" title="Дефицита нет" text="Запаса хватает по всем позициям." /></section>
          : <section className="card" data-testid="plan-list">
              {toShip.map(({ i, p }) => (
                <div className="row" key={i.nm_id + (p.size ?? '')}>
                  <span className="row-main">
                    <span className="row-title">{i.title ?? `Артикул WB ${i.nm_id}`}</span>
                    <span className="row-meta">запас <Num v={p.days_of_stock} /> · продажи <Num v={p.per_day} /> в день</span>
                  </span>
                  <span className="row-val"><span className="num"><span className="vh">везти </span><Num v={p.recommended} /></span></span>
                </div>
              ))}
            </section>}

      <Sect title="Параметры пополнения" />
      <section className="card">
        <Kv testid="plan-params" rows={[
          { label: 'Срок поставки', v: d.deadlines.lead_time },
          { label: 'Целевой запас', v: d.deadlines.target_days },
          { label: 'Страховой запас', v: d.deadlines.safety },
        ]} />
      </section>

      <Sect title="Коэффициенты приёмки складов" />
      <section className="card card-pad" data-testid="plan-acceptance">
        <p className="body-text"><ToneTag tone="attn" word="Недоступно" /></p>
        <p className="body-text">Wildberries временно отключил методы коэффициентов приёмки, тарифов на поставку и списка складов. Как только методы вернутся, раздел заработает без обновления приложения.</p>
        <button type="button" className="link-inline" data-testid="plan-why" onClick={() => open('S-39')}>Почему этого нет</button>
      </section>
    </div>
  )
}
